import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fab,
  Card,
  CardContent,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Switch,
  FormControlLabel,
  FormGroup,
  Badge,
  LinearProgress,
  Zoom,
  Fade,
  Slide,
  AppBar,
  Toolbar,
  useTheme,
  alpha,
  Tabs,
  Tab,
  InputAdornment,
  Collapse,
  Alert,
  AlertTitle,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Menu,
  MenuItem,
  Checkbox,
  Grid
} from '@mui/material';
import {
  ArrowBack,
  Notifications as NotificationsIcon,
  NotificationsActive,
  NotificationsOff,
  NotificationImportant,
  Send,
  Group,
  Person,
  Schedule,
  Check,
  Close,
  Delete,
  MoreVert,
  Refresh,
  FilterList,
  Info,
  Warning,
  Error as ErrorIcon,
  CheckCircle,
  Campaign,
  Message,
  Security,
  Update,
  Settings,
  VolumeUp,
  VolumeOff,
  DoNotDisturb,
  AccessTime,
  Today,
  Search,
  Clear,
  MarkEmailRead,
  MarkEmailUnread
} from '@mui/icons-material';
import { format, formatDistanceToNow, isToday, isYesterday, parseISO } from 'date-fns';
import { RootState } from '../store';
import { socketService } from '../services/socket';
import { enqueueSnackbar } from 'notistack';
import UserSelector from '../components/UserSelector';

interface PushNotification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'error' | 'success' | 'message' | 'security' | 'update';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  timestamp: Date;
  read: boolean;
  sender?: {
    id: string;
    name: string;
    avatar?: string;
  };
  recipient?: {
    id: string;
    name: string;
    type: 'user' | 'group' | 'all';
  };
  data?: any;
  actions?: Array<{
    label: string;
    action: string;
    primary?: boolean;
  }>;
  expiresAt?: Date;
}

interface NotificationSettings {
  enabled: boolean;
  sound: boolean;
  desktop: boolean;
  email: boolean;
  quietHours: {
    enabled: boolean;
    start: string;
    end: string;
  };
  categories: {
    messages: boolean;
    security: boolean;
    updates: boolean;
    alerts: boolean;
  };
}

const Notifications: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const user = useSelector((state: RootState) => state.auth.user);
  
  const [notifications, setNotifications] = useState<PushNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedNotification, setSelectedNotification] = useState<PushNotification | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedNotifications, setSelectedNotifications] = useState<Set<string>>(new Set());
  const [filterType, setFilterType] = useState<string>('all');
  
  // Notification settings
  const [settings, setSettings] = useState<NotificationSettings>({
    enabled: true,
    sound: true,
    desktop: true,
    email: false,
    quietHours: {
      enabled: false,
      start: '22:00',
      end: '08:00'
    },
    categories: {
      messages: true,
      security: true,
      updates: true,
      alerts: true
    }
  });
  
  // New notification form
  const [newNotification, setNewNotification] = useState({
    title: '',
    message: '',
    type: 'info' as PushNotification['type'],
    priority: 'normal' as PushNotification['priority'],
    recipientType: 'user' as 'user' | 'group' | 'all',
    expiresIn: 0
  });

  const notificationTypes = [
    { value: 'info', label: 'Information', icon: <Info />, color: '#2196f3' },
    { value: 'warning', label: 'Warning', icon: <Warning />, color: '#ff9800' },
    { value: 'error', label: 'Error', icon: <ErrorIcon />, color: '#f44336' },
    { value: 'success', label: 'Success', icon: <CheckCircle />, color: '#4caf50' },
    { value: 'message', label: 'Message', icon: <Message />, color: '#9c27b0' },
    { value: 'security', label: 'Security', icon: <Security />, color: '#ff5722' },
    { value: 'update', label: 'Update', icon: <Update />, color: '#00bcd4' }
  ];

  const priorities = [
    { value: 'low', label: 'Low', color: '#9e9e9e' },
    { value: 'normal', label: 'Normal', color: '#2196f3' },
    { value: 'high', label: 'High', color: '#ff9800' },
    { value: 'urgent', label: 'Urgent', color: '#f44336', pulse: true }
  ];

  useEffect(() => {
    loadNotifications();
    setupSocketListeners();
    requestNotificationPermission();
    
    return () => {
      cleanupSocketListeners();
    };
  }, []);

  const requestNotificationPermission = async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        enqueueSnackbar('Desktop notifications enabled', { variant: 'success' });
      }
    }
  };

  const setupSocketListeners = () => {
    const socket = socketService.getSocket();
    if (!socket) return;

    socket.on('pushNotification', (notification: PushNotification) => {
      setNotifications(prev => [notification, ...prev]);
      
      // Show desktop notification if enabled
      if (settings.desktop && 'Notification' in window && Notification.permission === 'granted') {
        const desktopNotif = new Notification(notification.title, {
          body: notification.message,
          icon: '/icon-192x192.png',
          badge: '/icon-72x72.png',
          tag: notification.id,
          requireInteraction: notification.priority === 'urgent'
        });
        
        desktopNotif.onclick = () => {
          window.focus();
          handleNotificationClick(notification);
        };
      }
      
      // Play sound if enabled
      if (settings.sound && notification.priority !== 'low') {
        playNotificationSound();
      }
      
      // Show snackbar
      const typeConfig = getTypeConfig(notification.type);
      enqueueSnackbar(notification.title, {
        variant: notification.type === 'error' ? 'error' : 
                 notification.type === 'warning' ? 'warning' :
                 notification.type === 'success' ? 'success' : 'info',
        action: notification.actions ? (
          <Button
            color="inherit"
            size="small"
            onClick={() => handleNotificationClick(notification)}
          >
            View
          </Button>
        ) : undefined
      });
    });

    socket.on('notificationRead', (notificationId: string) => {
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
    });
  };

  const cleanupSocketListeners = () => {
    const socket = socketService.getSocket();
    if (!socket) return;
    
    socket.off('pushNotification');
    socket.off('notificationRead');
  };

  const loadNotifications = async () => {
    setLoading(true);
    try {
      // Mock data for demonstration
      const mockNotifications: PushNotification[] = [
        {
          id: '1',
          title: 'New Message',
          message: 'You have received a new secure message from John Doe',
          type: 'message',
          priority: 'normal',
          timestamp: new Date(),
          read: false,
          sender: { id: '1', name: 'John Doe' },
          actions: [
            { label: 'View Message', action: 'view_message', primary: true },
            { label: 'Reply', action: 'reply' }
          ]
        },
        {
          id: '2',
          title: 'Security Alert',
          message: 'Unusual login activity detected from new device',
          type: 'security',
          priority: 'high',
          timestamp: new Date(Date.now() - 3600000),
          read: false,
          data: { device: 'iPhone 12', location: 'New York, NY' },
          actions: [
            { label: 'Review Activity', action: 'review', primary: true },
            { label: 'Secure Account', action: 'secure' }
          ]
        },
        {
          id: '3',
          title: 'System Update',
          message: 'A new version of the application is available',
          type: 'update',
          priority: 'low',
          timestamp: new Date(Date.now() - 86400000),
          read: true
        },
        {
          id: '4',
          title: 'Meeting Reminder',
          message: 'Team standup starts in 15 minutes',
          type: 'info',
          priority: 'urgent',
          timestamp: new Date(Date.now() - 900000),
          read: false,
          actions: [
            { label: 'Join Meeting', action: 'join_meeting', primary: true }
          ]
        }
      ];
      
      setNotifications(mockNotifications);
      setLoading(false);
    } catch (error) {
      console.error('Error loading notifications:', error);
      setLoading(false);
    }
  };

  const playNotificationSound = () => {
    const audio = new Audio('/notification.mp3');
    audio.play().catch(e => console.log('Could not play notification sound:', e));
  };

  const handleSendNotification = async () => {
    if (!newNotification.title || !newNotification.message) {
      enqueueSnackbar('Please fill in all required fields', { variant: 'warning' });
      return;
    }

    if (newNotification.recipientType !== 'all' && selectedUsers.length === 0) {
      enqueueSnackbar('Please select at least one recipient', { variant: 'warning' });
      return;
    }

    const socket = socketService.getSocket();
    if (!socket) return;

    const recipients = newNotification.recipientType === 'all' 
      ? ['all']
      : selectedUsers.map(u => u.Email);

    socket.emit('sendPushNotification', {
      Title: newNotification.title,
      Message: newNotification.message,
      Type: newNotification.type,
      Priority: newNotification.priority,
      Recipients: recipients,
      ExpiresIn: newNotification.expiresIn
    }, (response: any) => {
      if (response.success) {
        enqueueSnackbar('Notification sent successfully', { variant: 'success' });
        setSendDialogOpen(false);
        resetForm();
      } else {
        enqueueSnackbar(response.error || 'Failed to send notification', { variant: 'error' });
      }
    });
  };

  const resetForm = () => {
    setNewNotification({
      title: '',
      message: '',
      type: 'info',
      priority: 'normal',
      recipientType: 'user',
      expiresIn: 0
    });
    setSelectedUsers([]);
  };

  const handleNotificationClick = (notification: PushNotification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    setSelectedNotification(notification);
    setDetailsOpen(true);
  };

  const markAsRead = (notificationId: string) => {
    const socket = socketService.getSocket();
    if (!socket) return;

    socket.emit('markNotificationRead', { notificationId }, (response: any) => {
      if (response.success) {
        setNotifications(prev =>
          prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
        );
      }
    });
  };

  const markAllAsRead = () => {
    const unreadIds = notifications.filter(n => !n.read).map(n => n.id);
    if (unreadIds.length === 0) return;

    const socket = socketService.getSocket();
    if (!socket) return;

    socket.emit('markAllNotificationsRead', {}, (response: any) => {
      if (response.success) {
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
        enqueueSnackbar('All notifications marked as read', { variant: 'success' });
      }
    });
  };

  const deleteNotification = (notificationId: string) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
    enqueueSnackbar('Notification deleted', { variant: 'info' });
  };

  const deleteSelected = () => {
    setNotifications(prev => prev.filter(n => !selectedNotifications.has(n.id)));
    setSelectedNotifications(new Set());
    enqueueSnackbar(`${selectedNotifications.size} notifications deleted`, { variant: 'info' });
  };

  const getTypeConfig = (type: PushNotification['type']) => {
    return notificationTypes.find(t => t.value === type) || notificationTypes[0];
  };

  const getPriorityConfig = (priority: PushNotification['priority']) => {
    return priorities.find(p => p.value === priority) || priorities[1];
  };

  const filteredNotifications = notifications.filter(notification => {
    const matchesSearch = notification.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         notification.message.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesTab = selectedTab === 0 ||
                       (selectedTab === 1 && !notification.read) ||
                       (selectedTab === 2 && notification.priority === 'urgent' || notification.priority === 'high');
    
    const matchesType = filterType === 'all' || notification.type === filterType;
    
    return matchesSearch && matchesTab && matchesType;
  });

  const groupNotificationsByDate = (notifications: PushNotification[]) => {
    const groups: { [key: string]: PushNotification[] } = {};
    
    notifications.forEach(notification => {
      const date = new Date(notification.timestamp);
      let key: string;
      
      if (isToday(date)) {
        key = 'Today';
      } else if (isYesterday(date)) {
        key = 'Yesterday';
      } else {
        key = format(date, 'MMMM d, yyyy');
      }
      
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(notification);
    });
    
    return groups;
  };

  const renderNotificationCard = (notification: PushNotification) => {
    const typeConfig = getTypeConfig(notification.type);
    const priorityConfig = getPriorityConfig(notification.priority);
    const isSelected = selectedNotifications.has(notification.id);
    
    return (
      <Zoom in={true} key={notification.id}>
        <Card
          sx={{
            mb: 1.5,
            opacity: notification.read ? 0.8 : 1,
            background: notification.read
              ? theme.palette.background.paper
              : `linear-gradient(135deg, ${alpha(typeConfig.color, 0.05)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`,
            border: `1px solid ${notification.read ? alpha(theme.palette.divider, 0.1) : alpha(typeConfig.color, 0.3)}`,
            transition: 'box-shadow 0.2s ease, transform 0.2s ease, background 0.2s ease',
            cursor: 'pointer',
            position: 'relative',
            '&:hover': {
              transform: 'translateX(4px)',
              boxShadow: theme.shadows[4]
            }
          }}
        >
          {priorityConfig.pulse && !notification.read && (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: priorityConfig.color,
                animation: 'pulse 2s infinite'
              }}
            />
          )}
          
          <CardContent
            onClick={() => handleNotificationClick(notification)}
            sx={{ py: 1.5, px: 2 }}
          >
            <Box display="flex" alignItems="flex-start" gap={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isSelected}
                    onChange={(e) => {
                      e.stopPropagation();
                      const newSelected = new Set(selectedNotifications);
                      if (isSelected) {
                        newSelected.delete(notification.id);
                      } else {
                        newSelected.add(notification.id);
                      }
                      setSelectedNotifications(newSelected);
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                }
                label=""
                sx={{ m: 0, mr: -1 }}
              />
              
              <Avatar
                sx={{
                  bgcolor: alpha(typeConfig.color, 0.1),
                  color: typeConfig.color,
                  width: 40,
                  height: 40
                }}
              >
                {typeConfig.icon}
              </Avatar>
              
              <Box flex={1}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {notification.title}
                    </Typography>
                    {!notification.read && (
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          bgcolor: 'primary.main'
                        }}
                      />
                    )}
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    {notification.priority !== 'normal' && (
                      <Chip
                        size="small"
                        label={priorityConfig.label}
                        sx={{
                          height: 20,
                          fontSize: '0.7rem',
                          bgcolor: alpha(priorityConfig.color, 0.1),
                          color: priorityConfig.color,
                          fontWeight: 600
                        }}
                      />
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {formatDistanceToNow(new Date(notification.timestamp), { addSuffix: true })}
                    </Typography>
                  </Box>
                </Box>
                
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {notification.message}
                </Typography>
                
                {notification.sender && (
                  <Box display="flex" alignItems="center" gap={0.5} mt={1}>
                    <Person sx={{ fontSize: 14, color: 'text.disabled' }} />
                    <Typography variant="caption" color="text.secondary">
                      From {notification.sender.name}
                    </Typography>
                  </Box>
                )}
                
                {notification.actions && notification.actions.length > 0 && (
                  <Box display="flex" gap={1} mt={1.5}>
                    {notification.actions.map(action => (
                      <Button
                        key={action.action}
                        size="small"
                        variant={action.primary ? 'contained' : 'outlined'}
                        onClick={(e) => {
                          e.stopPropagation();
                          console.log('Action:', action.action);
                        }}
                        sx={{
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          py: 0.5,
                          px: 1.5
                        }}
                      >
                        {action.label}
                      </Button>
                    ))}
                  </Box>
                )}
              </Box>
              
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setAnchorEl(e.currentTarget);
                  setSelectedNotification(notification);
                }}
              >
                <MoreVert fontSize="small" />
              </IconButton>
            </Box>
          </CardContent>
        </Card>
      </Zoom>
    );
  };

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      {/* Header */}
      <AppBar position="static" elevation={0} sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
        <Toolbar>
          <IconButton edge="start" onClick={() => navigate('/dashboard')} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <NotificationsActive sx={{ mr: 2, color: 'primary.main' }} />
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600, color: 'text.primary' }}>
            Push Notifications
          </Typography>
          
          <Badge badgeContent={notifications.filter(n => !n.read).length} color="error" sx={{ mr: 2 }}>
            <IconButton onClick={loadNotifications}>
              <Refresh />
            </IconButton>
          </Badge>
          
          {selectedNotifications.size > 0 && (
            <>
              <Chip
                label={`${selectedNotifications.size} selected`}
                onDelete={() => setSelectedNotifications(new Set())}
                sx={{ mr: 2 }}
              />
              <IconButton onClick={deleteSelected} color="error">
                <Delete />
              </IconButton>
            </>
          )}
          
          <IconButton onClick={() => setSettingsOpen(true)}>
            <Settings />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Tabs and Filters */}
      <Paper sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }} elevation={0}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Tabs
            value={selectedTab}
            onChange={(e, newValue) => setSelectedTab(newValue)}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label={`All (${notifications.length})`} />
            <Tab label={`Unread (${notifications.filter(n => !n.read).length})`} />
            <Tab label={`Important (${notifications.filter(n => n.priority === 'high' || n.priority === 'urgent').length})`} />
          </Tabs>
          
          <Box display="flex" gap={1}>
            <Button
              size="small"
              startIcon={<MarkEmailRead />}
              onClick={markAllAsRead}
              disabled={notifications.filter(n => !n.read).length === 0}
            >
              Mark all as read
            </Button>
            <ToggleButtonGroup
              value={filterType}
              exclusive
              onChange={(e, newType) => newType && setFilterType(newType)}
              size="small"
            >
              <ToggleButton value="all">All</ToggleButton>
              {notificationTypes.slice(0, 4).map(type => (
                <ToggleButton key={type.value} value={type.value}>
                  <Tooltip title={type.label}>
                    {type.icon}
                  </Tooltip>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        </Box>
      </Paper>

      {/* Search */}
      <Box sx={{ p: 2, pb: 0 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search notifications..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchQuery('')}>
                  <Clear />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <LinearProgress sx={{ width: '50%' }} />
          </Box>
        ) : filteredNotifications.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <NotificationsOff sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No notifications
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchQuery ? 'Try adjusting your search' : 'You\'re all caught up!'}
            </Typography>
          </Box>
        ) : (
          Object.entries(groupNotificationsByDate(filteredNotifications)).map(([date, notifications]) => (
            <Box key={date} sx={{ mb: 3 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1, fontWeight: 600 }}
              >
                {date}
              </Typography>
              {notifications.map(notification => renderNotificationCard(notification))}
            </Box>
          ))
        )}
      </Box>

      {/* FAB */}
      <Zoom in={!loading}>
        <Fab
          color="primary"
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          }}
          onClick={() => setSendDialogOpen(true)}
        >
          <Send />
        </Fab>
      </Zoom>

      {/* Send Notification Dialog */}
      <Dialog
        open={sendDialogOpen}
        onClose={() => setSendDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Slide}
        TransitionProps={{ direction: 'up' } as any}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" fontWeight={600}>Send Push Notification</Typography>
            <IconButton onClick={() => setSendDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              fullWidth
              label="Title"
              value={newNotification.title}
              onChange={(e) => setNewNotification({ ...newNotification, title: e.target.value })}
            />
            
            <TextField
              fullWidth
              label="Message"
              value={newNotification.message}
              onChange={(e) => setNewNotification({ ...newNotification, message: e.target.value })}
              multiline
              rows={3}
            />
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  select
                  label="Type"
                  value={newNotification.type}
                  onChange={(e) => setNewNotification({ ...newNotification, type: e.target.value as any })}
                >
                  {notificationTypes.map(type => (
                    <MenuItem key={type.value} value={type.value}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box sx={{ color: type.color }}>{type.icon}</Box>
                        {type.label}
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  select
                  label="Priority"
                  value={newNotification.priority}
                  onChange={(e) => setNewNotification({ ...newNotification, priority: e.target.value as any })}
                >
                  {priorities.map(priority => (
                    <MenuItem key={priority.value} value={priority.value}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: priority.color
                          }}
                        />
                        {priority.label}
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
            
            <FormGroup>
              <Typography variant="subtitle2" gutterBottom>Recipients</Typography>
              <ToggleButtonGroup
                value={newNotification.recipientType}
                exclusive
                onChange={(e, newType) => newType && setNewNotification({ ...newNotification, recipientType: newType })}
                fullWidth
              >
                <ToggleButton value="user">
                  <Person sx={{ mr: 1 }} />
                  Specific Users
                </ToggleButton>
                <ToggleButton value="group">
                  <Group sx={{ mr: 1 }} />
                  Groups
                </ToggleButton>
                <ToggleButton value="all">
                  <Campaign sx={{ mr: 1 }} />
                  All Users
                </ToggleButton>
              </ToggleButtonGroup>
            </FormGroup>
            
            {newNotification.recipientType !== 'all' && (
              <UserSelector
                value={null}
                onChange={(user) => {
                  if (user) {
                    setSelectedUsers([user]);
                  }
                }}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setSendDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSendNotification}
            startIcon={<Send />}
            disabled={!newNotification.title || !newNotification.message}
          >
            Send Notification
          </Button>
        </DialogActions>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Notification Settings</DialogTitle>
        <DialogContent>
          <List>
            <ListItem>
              <ListItemText
                primary="Enable Notifications"
                secondary="Receive push notifications"
              />
              <Switch
                checked={settings.enabled}
                onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
              />
            </ListItem>
            
            <ListItem>
              <ListItemText
                primary="Sound"
                secondary="Play sound for new notifications"
              />
              <Switch
                checked={settings.sound}
                onChange={(e) => setSettings({ ...settings, sound: e.target.checked })}
              />
            </ListItem>
            
            <ListItem>
              <ListItemText
                primary="Desktop Notifications"
                secondary="Show notifications on desktop"
              />
              <Switch
                checked={settings.desktop}
                onChange={(e) => setSettings({ ...settings, desktop: e.target.checked })}
              />
            </ListItem>
            
            <ListItem>
              <ListItemText
                primary="Email Notifications"
                secondary="Receive notifications via email"
              />
              <Switch
                checked={settings.email}
                onChange={(e) => setSettings({ ...settings, email: e.target.checked })}
              />
            </ListItem>
            
            <Divider sx={{ my: 2 }} />
            
            <ListItem>
              <ListItemText
                primary="Quiet Hours"
                secondary={`Mute notifications from ${settings.quietHours.start} to ${settings.quietHours.end}`}
              />
              <Switch
                checked={settings.quietHours.enabled}
                onChange={(e) => setSettings({
                  ...settings,
                  quietHours: { ...settings.quietHours, enabled: e.target.checked }
                })}
              />
            </ListItem>
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)}>Close</Button>
          <Button variant="contained" onClick={() => {
            setSettingsOpen(false);
            enqueueSnackbar('Settings saved', { variant: 'success' });
          }}>
            Save Settings
          </Button>
        </DialogActions>
      </Dialog>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem onClick={() => {
          if (selectedNotification) {
            markAsRead(selectedNotification.id);
          }
          setAnchorEl(null);
        }}>
          <MarkEmailRead sx={{ mr: 1 }} fontSize="small" />
          Mark as read
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedNotification) {
            deleteNotification(selectedNotification.id);
          }
          setAnchorEl(null);
        }}>
          <Delete sx={{ mr: 1 }} fontSize="small" />
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Notifications;